// Registry-driven lever-subject detector. Given a user message, returns the
// value-creation lever family/families it is ABOUT — derived entirely from the
// governed lever taxonomy (family ids + labels + hypothesis hints), NOT from a
// hardcoded keyword list. This is the building block that lets a cold,
// out-of-thread single-lever question ("what is the network design lever
// worth?") engage the same value-creation composite path the flagship uses,
// instead of dropping to the generic company-profile fallback.
//
// Safety: this only IDENTIFIES a subject; the caller decides whether to engage
// the lever path. Wiring it at the fallback boundary is purely additive — it
// only affects questions that currently fail to route — so it cannot regress
// the flagship or any working objective.

export type LeverSubjectFamily = Readonly<{
  id: string;
  label: string;
  hypothesisHint?: string;
}>;

export type LeverSubjectMatch = Readonly<{
  familyId: string;
  score: number; // number of distinct governed terms matched
  matchedTerms: readonly string[];
}>;

// Tokens that carry no subject signal — excluded when deriving match terms from
// governed labels so we match on the meaningful nouns, not connective words.
const STOPWORDS = new Set([
  'and', 'or', 'the', 'a', 'an', 'of', 'to', 'in', 'on', 'for', 'with', 'into',
  'reduce', 'improve', 'lower', 'optimize', 'rationalize', 'consolidate',
  'shift', 'redesign', 'right', 'size', 'cut', 'recover', 'grow', 'across',
  'eligible', 'work', 'lever', 'levers', 'cost', 'costs', 'spend', 'savings',
  'program', 'programs', 'our', 'their', 'other', 'more', 'less',
]);

function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9&\s-]/g, ' ')
    .split(/[\s-]+/)
    .filter((t) => t.length >= 3 && !STOPWORDS.has(t));
}

// Governed match terms for a family: the meaningful tokens of its label, plus a
// couple of high-signal multi-word phrases. All derived from the registry.
function familyTerms(family: LeverSubjectFamily): Set<string> {
  const terms = new Set<string>();
  for (const tok of tokenize(family.label)) terms.add(tok);
  // The family id segments are also governed (e.g. network_optimization).
  for (const seg of family.id.split('_')) {
    if (seg.length >= 3 && !STOPWORDS.has(seg)) terms.add(seg);
  }
  return terms;
}

export function detectLeverSubjects(
  message: string,
  families: readonly LeverSubjectFamily[],
): readonly LeverSubjectMatch[] {
  const messageTokens = new Set(tokenize(message));
  if (messageTokens.size === 0) return [];
  const matches: LeverSubjectMatch[] = [];
  for (const family of families) {
    const terms = familyTerms(family);
    const matched: string[] = [];
    for (const term of terms) {
      if (messageTokens.has(term)) matched.push(term);
    }
    if (matched.length > 0) {
      matches.push(
        Object.freeze({
          familyId: family.id,
          score: matched.length,
          matchedTerms: Object.freeze(matched.sort()),
        }),
      );
    }
  }
  // Highest-signal first; ties broken by family id for determinism.
  return Object.freeze(
    matches.sort((a, b) => b.score - a.score || a.familyId.localeCompare(b.familyId)),
  );
}

// True when the message clearly references at least one governed lever family.
export function messageReferencesLeverSubject(
  message: string,
  families: readonly LeverSubjectFamily[],
): boolean {
  return detectLeverSubjects(message, families).length > 0;
}
